import styled from 'styled-components';
import type { ConsultationProductListModel } from 'autogen/swagger/all';
import { convertTimeSpanToString, dateTimeFormat } from 'helpers/DateTimeConverter';
import {
    ColorNordicGrey4,
    Spacing4,
    Spacing8,
    Spacing12,
    ColorNordicGrey2,
    Spacing6
} from 'autogen/design-tokens/tokens';
import { useTranslation } from 'react-i18next';
import * as transKey from 'autogen/translation-keys/trans-root-website';
import { LinkActionArticleCard } from 'components/ArticleCard/ArticleCard';
import Tag from 'components/generic/tag/Tag';
import { FreeLabel, NextEvent } from 'autogen/translation-keys/trans-website-booking-consultationlist-consultationitem';
import Tippy from '@tippyjs/react';
import { Typography } from 'components/generic/typography';
import { InBooth } from 'autogen/translation-keys/trans-website-booking-consultationproduct';
import Image from 'components/generic/Image';
import { sizes } from 'variables';
import { getLargestPictureSrc } from 'helpers/imageScalingHelper';
import { useGetBookingTypeTrans } from '../shared/useGetBookingTypeTrans';
import formatTitle from '../shared/formatTitle';
import Icon from 'components/Icon';

const AdditionalInfo = styled.div`
    display: grid;
    gap: ${Spacing8};
    margin: ${Spacing12} 0 ${Spacing8};
`;

const TagGrid = styled.div`
    display: grid;
    gap: ${Spacing6};
    grid-template-columns: repeat(2, max-content);
`;

const StyledTag = styled(Tag)`
    background-color: ${ColorNordicGrey2};
`;

const ImageContainer = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 6 / 5;
    background-color: ${ColorNordicGrey4};
`;

const StyledImage = styled(Image.WithScaling)`
    position: absolute;
    object-fit: cover;
    object-position: center;
    height: 100%;
    width: 100%;
`;

const TooltipContentContainer = styled.div`
    max-width: 240px;
`;

const PriceInfoContainer = styled.span`
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
`;

const InfoCircle = styled(Icon)`
    margin-left: ${Spacing4};
`;

export interface IConsultationProductItem {
    item: ConsultationProductListModel;
}

function formatEventStartTime(startTime: string) {
    const formattedStartTime = dateTimeFormat({
        dateTime: startTime,
        options: { weekday: 'long', year: '2-digit', month: 'long', day: 'numeric' }
    }).replace(' den ', ' d. ');

    return formattedStartTime;
}

export default function ConsultationProductItem({ item }: IConsultationProductItem): JSX.Element {
    const {
        linkAction,
        image,
        duration,
        title,
        nextAvailableBookingSlot,
        externalPartner,
        bookingType,
        datalayer,
        suggestedPriceText,
        priceDisclaimer,
        category,
        inBooth,
        isVirtual
    } = item;
    const { t } = useTranslation();
    const { startTime } = nextAvailableBookingSlot || {};
    const { name: externalPartnerName } = externalPartner || {};

    const {
        Shared: { TitleWithPartnerName },
        ConsultationList: {
            ConsultationItem: { ReadMore }
        }
    } = transKey.Booking;

    const productTitle = formatTitle({
        productName: title,
        externalPartnerName,
        translation: t(TitleWithPartnerName)
    });

    return (
        <LinkActionArticleCard
            action={linkAction}
            tag={category.name}
            {...(inBooth && { secondaryTag: t(InBooth) })}
            textBox={{
                overline: useGetBookingTypeTrans({ isVirtual, bookingType }),
                title: productTitle,
                additionalInfo: (
                    <AdditionalInfo>
                        {bookingType === 'Event' && startTime && (
                            <div>
                                {t(NextEvent)} {formatEventStartTime(startTime)}
                            </div>
                        )}
                        <TagGrid>
                            <StyledTag>
                                {convertTimeSpanToString({
                                    timeSpan: duration
                                })}
                            </StyledTag>
                            {priceDisclaimer ? (
                                <Tippy
                                    maxWidth={230}
                                    theme="plain-tooltip"
                                    placement="bottom"
                                    content={
                                        <TooltipContentContainer>
                                            <Typography.Body size="S" as="span">
                                                *{priceDisclaimer}
                                            </Typography.Body>
                                        </TooltipContentContainer>
                                    }
                                    touch="hold"
                                    popperOptions={{
                                        modifiers: [
                                            {
                                                name: 'flip',
                                                enabled: false
                                            }
                                        ]
                                    }}
                                >
                                    <PriceInfoContainer>
                                        <StyledTag>{suggestedPriceText}*</StyledTag>
                                        <InfoCircle type="Information" size={16} />
                                    </PriceInfoContainer>
                                </Tippy>
                            ) : (
                                <StyledTag>{suggestedPriceText || t(FreeLabel)}</StyledTag>
                            )}
                        </TagGrid>
                    </AdditionalInfo>
                ),
                action: {
                    buttonText: t(ReadMore)
                }
            }}
            data-datalayer={JSON.stringify(datalayer)}
        >
            <ImageContainer>
                {image && (
                    <StyledImage
                        alt=""
                        sizes={[[sizes.md, 350], 250]}
                        src={getLargestPictureSrc(image)}
                        transformType="WidthScale"
                    />
                )}
            </ImageContainer>
        </LinkActionArticleCard>
    );
}
